import { createContext, useCallback, useEffect, useState } from "react";
import { IProduto } from "shared/interfaces/IProduto";
import { CartProps, CartContextData, CartProviderProps } from "shared/interfaces/ICart";
import { formatarMoeda } from "shared/utils/money";
import { useAuth } from "../Auth/AuthContext";
import { listarCarrinho, adicionarItemCarrinho, alterarQuantidadeCarrinho, removerItemCarrinho } from "shared/services/api/cart.api";

export const CartContext = createContext({} as CartContextData);

export function CartProvider({ children }: CartProviderProps) {
  const { signed, loadingAuth } = useAuth();
  const [cart, setCart] = useState<CartProps[]>([]);
  const [total, setTotal] = useState("R$ 0,00");

  const calcularSubtotal = (items: CartProps[]) => {
    const soma = items.reduce((acc, item) => acc + item.total, 0);
    setTotal(formatarMoeda(soma));
  };

  const carregarCarrinho = useCallback(async () => {
    if (loadingAuth || !signed) {
      setCart([]);
      calcularSubtotal([]);
      return;
    }
    try {
      const response = await listarCarrinho();
      const itens: CartProps[] = (response.items ?? []).map((item: any) => ({
        id: item.lineId ?? item.productId,
        combinationId: item.combinationId ?? null,
        variacoes: item.attributes ?? [],
        motivoIndisponivel: item.unavailableReason ?? null,
        productId: item.productId,
        storeId: item.storeId,
        name: item.name,
        preco: Number(item.price),
        unidades: Number(item.quantity),
        estoque: Number(item.stock),
        frete: item.frete ?? 0,
        total: Number(item.total),
        gallery: item.gallery?.[0],
        available: item.available,
      }));
      setCart(itens);
      calcularSubtotal(itens);
    } catch (error) {
      console.error("Erro ao carregar carrinho:", error);
      setCart([]);
      calcularSubtotal([]);
    }
  }, [loadingAuth, signed]);

  useEffect(() => {
    if (loadingAuth) return;
    if (!signed) {
      setCart([]);
      calcularSubtotal([]);
      return;
    }
    carregarCarrinho();
  }, [signed, loadingAuth, carregarCarrinho]);

  async function addItemCart(produto: IProduto, quantidade: number = 1, combinationId: string | null = null) {
    if (loadingAuth || !signed) return;
    try {
      await adicionarItemCarrinho(produto.id, quantidade, combinationId);
      await carregarCarrinho();
    } catch (error) {
      console.error("Erro ao adicionar item:", error);
    }
  }

  async function increaseItem(produtoId: string) {
    if (loadingAuth || !signed) return;
    try {
      const item = cart.find((p) => p.id === produtoId);
      if (!item) return;
      await alterarQuantidadeCarrinho(produtoId, item.unidades + 1);
      await carregarCarrinho();
    } catch (error) {
      console.error("Erro ao aumentar quantidade:", error);
    }
  }

  async function decreaseItem(produtoId: string) {
    if (loadingAuth || !signed) return;
    try {
      const item = cart.find((p) => p.id === produtoId);
      if (!item) return;
      if (item.unidades <= 1) {
        await removerItemCarrinho(produtoId);
      } else {
        await alterarQuantidadeCarrinho(produtoId, item.unidades - 1);
      }
      await carregarCarrinho();
    } catch (error) {
      console.error("Erro ao diminuir quantidade:", error);
    }
  }

  async function removeItemCart(produtoId: string) {
    if (loadingAuth || !signed) return;
    try {
      await removerItemCarrinho(produtoId);
      await carregarCarrinho();
    } catch (error) {
      console.error("Erro ao remover item:", error);
    }
  }

  async function clearCart() {
    if (loadingAuth || !signed) return;
    try {
      if (cart.length === 0) {
        setCart([]);
        calcularSubtotal([]);
        return;
      }
      await Promise.all(cart.map((item) => removerItemCarrinho(item.id)));
      setCart([]);
      calcularSubtotal([]);
    } catch (error) {
      console.error("Erro ao limpar carrinho:", error);
      await carregarCarrinho();
    }
  }

  return (
    <CartContext.Provider value={{
      cart, cartQtd: cart.length, total,
      addItemCart, increaseItem, decreaseItem,
      removeItemCart, reloadCart: carregarCarrinho,
      clearCart, }}>
      {children}
    </CartContext.Provider>
  );
}